import { useCallback } from "react";
import { SphereCircularProgress } from "@components/common/sphere-circular-progress";
import { useNetworkConnectionContext } from "@context-providers/network-connection/network-connection-context";
import { BaseProjectIdProps } from "@custom-types/base-types";
import { assert } from "@faro-lotv/foundation";
import { FaroButton } from "@components/common/faro-button";
import { FaroTextButton } from "@components/common/faro-text-button";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Box, Stack } from "@mui/material";
import { WorkflowState } from "@pages/data-management/data-management-types";
import { CancelImportButton } from "@pages/data-management/import-data/cancel-import-button";
import { sphereColors } from "@styles/common-colors";
import { MIN_UPLOADING_PROGRESS } from "@utils/core-file-uploader";
import { bytesToSizeString } from "@utils/file-utils";
import { getDevButtonStyle } from "@pages/data-management/data-management-ui-utils";
import { useAppSelector } from "@store/store-helper";
import { isDevModeEnabledSelector } from "@store/app/app-selector";

/** @returns The value to be used for the `subtitle`. */
export function getSubtitle(
  state: "uploading" | "processing" | "registering", progress: number | undefined
): string | undefined {
  if (!progress) {
    if (state === "uploading") {
      return "Your data is being prepared for upload";
    } else if (state === "processing") {
      return "Processing is being prepared";
    } else if (state === "registering") {
      return "Registration is being prepared";
    } else {
      return undefined;
    }
  } else if (progress >= 100) {
    return "Finishing";
  } else {
    return (state === "uploading") ? "In progress" : undefined;
  }
}

interface Props extends BaseProjectIdProps {
  /** Main state of the Data Management workflow. See DataManagementWorkflow. */
  state: WorkflowState;
  /** The size in bytes of all selected files to upload. */
  totalFileSize: number,
  /** The upload speed in MB/s. */
  uploadSpeed: number;
  /** Upload progress value from 0 to 100. */
  uploadProgress: number;
  /** Processing progress value from 0 to 100. */
  processProgress: number;
  /** Registration progress value from 0 to 100. */
  registerProgress: number;
  /** boolean indicating if we have unsupported scans */
  hasUnsupportedScans: boolean;
  /** Sets the flag whether the user has closed the wizard view. In this case, the table view is shown. */
  setHasClosedWizardView: React.Dispatch<React.SetStateAction<boolean>>;
  /** Callback to set whether to prevent the redirect to the Viewer when the published state is reached. */
  setShouldPreventRedirect: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * The right panel of the wizard view.
 * Contains the progress of the current workflow state, in particular a progress circle.
 */
export function WizardRightPanel({
  projectId,
  state,
  totalFileSize,
  uploadSpeed,
  uploadProgress,
  processProgress,
  registerProgress,
  hasUnsupportedScans = false,
  setHasClosedWizardView,
  setShouldPreventRedirect,
}: Props): JSX.Element {
  const { isScreenXsAndSmall } = useMediaQueryList();
  const { isOnline } = useNetworkConnectionContext();
  const isDevModeEnabled = useAppSelector(isDevModeEnabledSelector);

  let title: string;
  let progress: number | undefined;
  let speedInfo: string | undefined;
  let subtitle: string | undefined;

  // The wizard is currently only visible during the in-progress and uploaded states.
  assert(state === "uploading" || state === "uploaded" || state === "processing" || state ===  "registering" ||
    state ===  "publishing", `WizardRightPanel: Unexpected state: ${state}`);

  // if "uploaded" and hasUnsupportedScans we need to show the close btn, otherwise the user gets blocked with possible action
  const hasCloseBtn = state === "uploaded" && hasUnsupportedScans;

  if (state === "uploading") {
    title = "Uploading";
    // Round tiny values to 0 to avoid displaying something like "52 Bytes" while we only have fake progress.
    if (uploadProgress && uploadProgress < MIN_UPLOADING_PROGRESS) {
      uploadProgress = 0;
    }
    progress = uploadProgress ?? undefined;
    subtitle = getSubtitle(state, progress);

    const speedStr = (isOnline && 0 < uploadProgress) ? `${uploadSpeed.toFixed(1)} MB/s` : "0 MB/s";
    if (0 < totalFileSize) {
      // We should calculate only with integer byte values.
      const uploadedFileSize = Math.round((uploadProgress / 100) * totalFileSize);
      const currentSizeStr = bytesToSizeString(uploadedFileSize);
      const totalSizeStr = bytesToSizeString(totalFileSize);
      speedInfo = `${currentSizeStr} / ${totalSizeStr} (${speedStr})`;
    } else {
      speedInfo = `${speedStr}`;
    }
  } else if (state === "uploaded") {
    title = "Upload finished";
    subtitle = getSubtitle("processing", 0);
    progress = uploadProgress;
  } else if (state === "processing") {
    title = "Processing";
    progress = processProgress ?? undefined;
    subtitle = getSubtitle(state, progress);
  } else if (state === "registering") {
    title = "Registering";
    progress = registerProgress ?? undefined;
    subtitle = getSubtitle(state, progress);
  } else {
    state satisfies "publishing";
    title = "Publishing";
  }

  // Round 0.00001 (MIN_PROGRESS_STARTED) percent to 0.
  // Other values are expected to be integers, but does no harm to round again.
  if (progress) {
    progress = Math.round(progress);
  }

  const onClose = useCallback((): void => {
    setHasClosedWizardView(true);
  }, [setHasClosedWizardView]);

  let actionButton;
  if (state !== "uploaded") {
    actionButton = (
      <CancelImportButton
        projectId={projectId}
        state={state}
        isFromErrorView={false}
        setShouldPreventRedirect={setShouldPreventRedirect}
      />
    );
  } else if (isDevModeEnabled) {
    // dev close btn should only be visible when in "uploaded" state
    // e.g. we should be able to cancel the upload (CancelImportButton when in uploading state)
    actionButton = (
      <FaroButton
        onClick={onClose}
        sx={getDevButtonStyle()}
        shouldNotTranslate={true}
      >
        Close (Dev)
      </FaroButton>
    );
  } else if (hasCloseBtn) {
    actionButton = (
      <SphereTooltip title="Close">
        <FaroTextButton
          onClick={onClose}
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            backgroundColor: sphereColors.gray50,
            paddingLeft: "25px",
            paddingRight: "25px",
          }}
        >
          Close
        </FaroTextButton>
      </SphereTooltip>
    );
  }

  return (
    <Stack sx={{
      width: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "30px",
      backgroundColor: sphereColors.pureWhite,
      border: `1px solid ${sphereColors.gray200}`,
    }}>
      <Box
        sx={{
          width: "100%",
        }}
      >
        {/* Progress spinner */}
        <Box
          data-testid="sa-wrp-progress"
          sx={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "30px",
          }}
        >
          <SphereCircularProgress
            value={progress}
            size={150}
            sxValue={{
              fontSize: "32px",
              fontWeight: "600",
            }}
            strokeLinecap="square"
          />
        </Box>

        {/* Title */}
        <Box
          sx={{
            fontSize: "32px",
            fontWeight: "600",
            display: "flex",
            justifyContent: "center",
            marginBottom: "20px",
          }}
        >
          {title}
        </Box>

        {/* Subtitle */}
        <Box
          data-testid="sa-wrp-subtitle"
          sx={{
            fontSize: "14px",
            color: sphereColors.gray600,
            width: "100%",
            display: "flex",
            justifyContent: "center",
            marginBottom: "40px",
          }}
        >
          {!isScreenXsAndSmall &&
            <>
              <span>{subtitle}</span>
              { !!speedInfo &&
                <span style={{ marginLeft: "15px", marginRight: "15px" }}>•</span>
              }
            </>
          }
          { !!speedInfo &&
            <var>{speedInfo}</var>
          }
        </Box>

        {/* Buttons */}
        <Stack direction="row"
          gap="15px"
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            marginBottom: "10px",
          }}
        >
          {/* Not yet implemented. */}
          {/* <FaroTextButton
            startIcon={<PauseSvg/>}
            onClick={() => {}}
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              backgroundColor: sphereColors.gray50,
              paddingLeft: "15px",
              paddingRight: "15px",
            }}
          >
            Pause
          </FaroTextButton> */}
          
          {actionButton}
        </Stack>
      </Box>
    </Stack>
  );
}